export interface TimeRemaining {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    totalSeconds: number;
}

/**
 * Calculates the time remaining until a target date.
 */
export const getTimeRemaining = (targetDate: string | Date): TimeRemaining => {
    const total = Date.parse(targetDate.toString()) - Date.now();

    if (total <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, totalSeconds: 0 };
    }

    const seconds = Math.floor((total / 1000) % 60);
    const minutes = Math.floor((total / 1000 / 60) % 60);
    const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
    const days = Math.floor(total / (1000 * 60 * 60 * 24));

    return {
        days,
        hours,
        minutes,
        seconds,
        totalSeconds: Math.floor(total / 1000)
    };
};

/**
 * Formats a TimeRemaining object into a human-readable string.
 * Example: "2d 12h 23m 56s"
 */
export const formatCountdown = (time: TimeRemaining): string => {
    const parts = [];
    if (time.days > 0) parts.push(`${time.days}d`);
    if (time.hours > 0 || time.days > 0) parts.push(`${time.hours}h`);
    if (time.minutes > 0 || time.hours > 0 || time.days > 0) parts.push(`${time.minutes}m`);
    parts.push(`${time.seconds}s`);
    return parts.join(' ');
};

/**
 * Checks if a date is in the past.
 */
export const isPast = (date: string | Date): boolean => {
    return Date.parse(date.toString()) < Date.now();
};

/**
 * Checks if a date is in the future.
 */
export const isFuture = (date: string | Date): boolean => {
    return Date.parse(date.toString()) > Date.now();
};

/**
 * Checks if the current time is between two dates.
 */
export const isBetween = (startDate: string | Date, endDate: string | Date): boolean => {
    const now = Date.now();
    return Date.parse(startDate.toString()) <= now && now <= Date.parse(endDate.toString());
};
